import { useCallback, useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';

const getPublicationBySource = (participant, source) => (
  participant.getTrackPublication
    ? participant.getTrackPublication(source)
    : Array.from(participant.trackPublications.values()).find((publication) => publication.source === source)
);

const getVideoTrackFromPublication = (publication) => publication?.videoTrack || publication?.track || null;
const getAudioTrackFromPublication = (publication) => publication?.audioTrack || publication?.track || null;

const buildParticipantTiles = (participant, activeSpeakerIds) => {
  const cameraPublication = getPublicationBySource(participant, Track.Source.Camera);
  const microphonePublication = getPublicationBySource(participant, Track.Source.Microphone);
  const screenSharePublication = getPublicationBySource(participant, Track.Source.ScreenShare);

  const cameraTrack = getVideoTrackFromPublication(cameraPublication);
  const audioTrack = getAudioTrackFromPublication(microphonePublication);
  const screenTrack = getVideoTrackFromPublication(screenSharePublication);
  const cameraEnabled = Boolean(cameraTrack && !cameraPublication?.isMuted);
  const screenShareEnabled = Boolean(screenTrack && !screenSharePublication?.isMuted);
  const microphoneEnabled = Boolean(microphonePublication && !microphonePublication.isMuted);
  const baseParticipant = {
    identity: participant.identity,
    name: participant.name || participant.identity || 'Participant',
    isLocal: Boolean(participant.isLocal),
    isSpeaking: activeSpeakerIds.has(participant.identity),
    cameraEnabled,
    microphoneEnabled,
    screenShareEnabled,
  };
  const tiles = [];

  if (screenShareEnabled) {
    tiles.push({
      ...baseParticipant,
      tileId: `${participant.identity}:screen`,
      tileType: 'screen',
      videoTrack: screenTrack,
      audioTrack: cameraEnabled ? null : audioTrack,
    });
  }

  if (cameraEnabled || !screenShareEnabled) {
    tiles.push({
      ...baseParticipant,
      tileId: `${participant.identity}:camera`,
      tileType: 'camera',
      videoTrack: cameraTrack,
      audioTrack,
    });
  }

  return tiles;
};

const sortParticipants = (participants) => (
  [...participants].sort((left, right) => {
    if (left.tileType !== right.tileType) {
      return left.tileType === 'screen' ? -1 : 1;
    }
    if (left.isLocal !== right.isLocal) {
      return left.isLocal ? -1 : 1;
    }
    return left.name.localeCompare(right.name);
  })
);

const formatMediaWarning = (label, error) => {
  if (!error) {
    return `${label} access was unavailable`;
  }

  if (typeof error === 'string') {
    return `${label} access was unavailable: ${error}`;
  }

  if (error?.message) {
    return `${label} access was unavailable: ${error.message}`;
  }

  try {
    const serializedError = JSON.stringify(error);
    if (serializedError && serializedError !== '{}') {
      return `${label} access was unavailable: ${serializedError}`;
    }
  } catch (serializationError) {
    // Ignore serialization failures and fall back to the generic label.
  }

  return `${label} access was unavailable`;
};

export const useRoomCall = () => {
  const roomRef = useRef(null);
  const listenersRef = useRef([]);

  const [participants, setParticipants] = useState([]);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [microphoneEnabled, setMicrophoneEnabled] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [screenShareEnabled, setScreenShareEnabled] = useState(false);

  const syncParticipants = useCallback((targetRoom = roomRef.current) => {
    if (!targetRoom) {
      setParticipants([]);
      setMicrophoneEnabled(false);
      setCameraEnabled(false);
      setScreenShareEnabled(false);
      return;
    }

    const activeSpeakerIds = new Set((targetRoom.activeSpeakers || []).map((participant) => participant.identity));
    const nextParticipants = sortParticipants([
      ...buildParticipantTiles(targetRoom.localParticipant, activeSpeakerIds),
      ...Array.from(targetRoom.remoteParticipants.values()).flatMap((participant) => (
        buildParticipantTiles(participant, activeSpeakerIds)
      )),
    ]);

    const localParticipant = nextParticipants.find((participant) => participant.isLocal && participant.tileType === 'camera')
      || nextParticipants.find((participant) => participant.isLocal);
    setParticipants(nextParticipants);
    setMicrophoneEnabled(Boolean(localParticipant?.microphoneEnabled));
    setCameraEnabled(Boolean(localParticipant?.cameraEnabled));
    setScreenShareEnabled(Boolean(localParticipant?.screenShareEnabled));
  }, []);

  const detachRoomListeners = useCallback(() => {
    if (!roomRef.current) return;
    listenersRef.current.forEach(({ event, handler }) => {
      roomRef.current.off(event, handler);
    });
    listenersRef.current = [];
  }, []);

  const disconnect = useCallback(async () => {
    const activeRoom = roomRef.current;
    if (!activeRoom) return;

    detachRoomListeners();
    await activeRoom.disconnect();
    roomRef.current = null;
    setParticipants([]);
    setConnectionState('disconnected');
    setMicrophoneEnabled(false);
    setCameraEnabled(false);
    setScreenShareEnabled(false);
  }, [detachRoomListeners]);

  const connectToCall = useCallback(async ({ url, token }) => {
    if (!url || !token) {
      throw new Error('Missing call connection details');
    }

    if (roomRef.current) {
      await disconnect();
    }

    const liveRoom = new Room({
      adaptiveStream: true,
      dynacast: true,
      stopLocalTrackOnUnpublish: true,
    });

    roomRef.current = liveRoom;
    setConnectionState('connecting');

    const refreshState = () => syncParticipants(liveRoom);
    const listeners = [
      [RoomEvent.ParticipantConnected, refreshState],
      [RoomEvent.ParticipantDisconnected, refreshState],
      [RoomEvent.TrackSubscribed, refreshState],
      [RoomEvent.TrackUnsubscribed, refreshState],
      [RoomEvent.TrackMuted, refreshState],
      [RoomEvent.TrackUnmuted, refreshState],
      [RoomEvent.LocalTrackPublished, refreshState],
      [RoomEvent.LocalTrackUnpublished, refreshState],
      [RoomEvent.ActiveSpeakersChanged, refreshState],
      [RoomEvent.Reconnecting, () => setConnectionState('reconnecting')],
      [RoomEvent.Reconnected, () => {
        setConnectionState('connected');
        refreshState();
      }],
      [RoomEvent.Disconnected, () => {
        setConnectionState('disconnected');
        syncParticipants(null);
      }],
    ];

    listeners.forEach(([event, handler]) => {
      liveRoom.on(event, handler);
      listenersRef.current.push({ event, handler });
    });

    await liveRoom.connect(url, token);
    setConnectionState('connected');

    const mediaWarnings = [];

    try {
      await liveRoom.localParticipant.setMicrophoneEnabled(true);
    } catch (microphoneError) {
      mediaWarnings.push(formatMediaWarning('Microphone', microphoneError));
    }

    try {
      await liveRoom.localParticipant.setCameraEnabled(true);
    } catch (cameraError) {
      mediaWarnings.push(formatMediaWarning('Camera', cameraError));
    }

    refreshState();
    return { warnings: mediaWarnings };
  }, [disconnect, syncParticipants]);

  const toggleMicrophone = useCallback(async () => {
    if (!roomRef.current) return;
    const nextValue = !microphoneEnabled;
    await roomRef.current.localParticipant.setMicrophoneEnabled(nextValue);
    setMicrophoneEnabled(nextValue);
    syncParticipants();
  }, [microphoneEnabled, syncParticipants]);

  const toggleCamera = useCallback(async () => {
    if (!roomRef.current) return;
    const nextValue = !cameraEnabled;
    await roomRef.current.localParticipant.setCameraEnabled(nextValue);
    setCameraEnabled(nextValue);
    syncParticipants();
  }, [cameraEnabled, syncParticipants]);

  const toggleScreenShare = useCallback(async () => {
    if (!roomRef.current) return;
    const localParticipant = roomRef.current.localParticipant;
    if (typeof localParticipant.setScreenShareEnabled !== 'function') {
      throw new Error('Screen sharing is not supported in this browser');
    }

    const nextValue = !screenShareEnabled;
    await localParticipant.setScreenShareEnabled(nextValue);
    setScreenShareEnabled(nextValue);
    syncParticipants();
  }, [screenShareEnabled, syncParticipants]);

  useEffect(() => () => {
    disconnect().catch(() => {});
  }, [disconnect]);

  return {
    participants,
    participantCount: participants.length,
    connectionState,
    microphoneEnabled,
    cameraEnabled,
    screenShareEnabled,
    screenShareSupported: typeof window !== 'undefined' && Boolean(window.navigator?.mediaDevices?.getDisplayMedia),
    connectToCall,
    disconnect,
    toggleMicrophone,
    toggleCamera,
    toggleScreenShare,
  };
};

export default useRoomCall;
